import { ReactNode, useCallback, useState, useEffect } from 'react';
import { EmployeesContext } from './EmployeesContextObject';
import { Employee, EmployeeRow } from '../types';
import { fetchAllEmployees, addEmployee as apiAddEmployee } from '../api';
import { supabase } from '@cleanpro/common-ui';

export const EmployeesDataProvider = ({ children }: { children: ReactNode }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const transformEmployee = (row: EmployeeRow): Employee => ({
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    role: row.role,
    status: row.status as Employee['status'],
    hireDate: row.hire_date,
    hourlyRate: row.hourly_rate || 0,
    userId: row.user_id || null
  });

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllEmployees();
      const transformedEmployees = data?.map(transformEmployee) || [];
      setEmployees(transformedEmployees);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load employees on mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAllEmployees();
        const transformedEmployees = data?.map(transformEmployee) || [];
        if (isMounted) {
          setEmployees(transformedEmployees);
        }
      } catch (err) {
        console.error('Error loading employees:', err);
        if (isMounted) {
          setError('Failed to load employees');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Note: Real-time subscription removed - employee data changes infrequently.
  // Data is refetched on component mount and when needed.

  const addEmployee = useCallback(async (employee: Omit<Employee, 'id'>) => {
    try {
      const newEmployeeData = await apiAddEmployee(employee);
      const newEmployee = transformEmployee(newEmployeeData);
      setEmployees(prev => [...prev, newEmployee]);
    } catch (err) {
      console.error('Error adding employee:', err);
      throw err;
    }
  }, []);

  const updateEmployee = useCallback(async (employeeId: string, employeeData: Partial<Omit<Employee, 'id'>>) => {
    const { error } = await supabase
      .from('employees')
      .update({
        name: employeeData.name,
        email: employeeData.email,
        phone: employeeData.phone,
        role: employeeData.role,
        status: employeeData.status,
        hire_date: employeeData.hireDate,
        hourly_rate: employeeData.hourlyRate,
        user_id: employeeData.userId
      })
      .eq('id', employeeId);
      
    if (error) throw error;
    await loadEmployees(); // Refresh the list
  }, [loadEmployees]);

  const deleteEmployee = useCallback(async (employeeId: string) => {
    const { error } = await supabase
      .from('employees')
      .delete()
      .eq('id', employeeId);
      
    if (error) throw error;
    setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
  }, []);

  const value = {
    employees,
    loading,
    error,
    addEmployee,
    updateEmployee,
    deleteEmployee
  };

  return (
    <EmployeesContext.Provider value={value}>
      {children}
    </EmployeesContext.Provider>
  );
};